import { ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";

import { PaymentStatus, PaymentType } from "@/api-v3";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import { getPaymentTitle } from "../../../helpers/get-payment-title";
import { usePaymentFilters } from "../../../hooks/use-payment-filters";

const ALL_VALUE = "__all__";

// A single user-facing option may map to more than one backend status.
// The "approved" option covers admin-approved claims (approved) and
// participant-accepted stipends/micropayments (accepted); its label is the
// per-status labels joined (e.g. "Approved/Accepted").
const STATUS_FILTERS = [
  { key: "pending", statuses: [PaymentStatus.pending] },
  {
    key: "approved",
    statuses: [PaymentStatus.approved, PaymentStatus.accepted],
  },
  { key: "rejected", statuses: [PaymentStatus.rejected] },
] as const;

const TYPE_OPTIONS = Object.values(PaymentType);

function getActiveStatusKey(status: PaymentStatus[]): string {
  if (status.length === 0) {
    return ALL_VALUE;
  }
  const match = STATUS_FILTERS.find(
    (filter) =>
      filter.statuses.length === status.length &&
      filter.statuses.every((value) => status.includes(value))
  );
  return match?.key ?? ALL_VALUE;
}

export function PaymentFilters() {
  const { t } = useTranslation();
  const { status, types, setStatus, setTypes } = usePaymentFilters();

  const formatStatusLabel = (statuses: readonly PaymentStatus[]) =>
    statuses.map((value) => t(value)).join("/");

  const activeStatusKey = getActiveStatusKey(status);
  const activeStatusFilter = STATUS_FILTERS.find(
    (filter) => filter.key === activeStatusKey
  );
  const statusLabel = activeStatusFilter
    ? formatStatusLabel(activeStatusFilter.statuses)
    : t("status");
  const typeLabel =
    types.length === 0
      ? t("type")
      : types.length === 1
        ? getPaymentTitle(types[0], t)
        : t("type") + ` (${types.length})`;

  const handleStatusChange = (value: string) => {
    const filter = STATUS_FILTERS.find((option) => option.key === value);
    setStatus(filter ? [...filter.statuses] : []);
  };

  const handleTypeToggle = (type: PaymentType, checked: boolean) => {
    if (checked) {
      setTypes([...types, type]);
    } else {
      setTypes(types.filter((value) => value !== type));
    }
  };

  return (
    <div className="flex gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            {statusLabel}
            <ChevronDown className="ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          <DropdownMenuRadioGroup
            value={activeStatusKey}
            onValueChange={handleStatusChange}
          >
            <DropdownMenuRadioItem value={ALL_VALUE}>
              {t("all")}
            </DropdownMenuRadioItem>
            {STATUS_FILTERS.map((filter) => (
              <DropdownMenuRadioItem key={filter.key} value={filter.key}>
                {formatStatusLabel(filter.statuses)}
              </DropdownMenuRadioItem>
            ))}
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm">
            {typeLabel}
            <ChevronDown className="ml-1" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start">
          {TYPE_OPTIONS.map((option) => (
            <DropdownMenuCheckboxItem
              key={option}
              checked={types.includes(option)}
              onCheckedChange={(checked) =>
                handleTypeToggle(option, checked === true)
              }
              onSelect={(event) => event.preventDefault()}
            >
              {getPaymentTitle(option, t)}
            </DropdownMenuCheckboxItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
